import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    MdSearch,
    MdCalendarToday,
    MdCreditCard,
    MdMoney,
    MdTrendingUp,
    MdTrendingDown,
    MdAccountBalanceWallet,
    MdDescription,
    MdClose,
    MdKeyboardArrowLeft,
    MdKeyboardArrowRight,
    MdReceiptLong
} from 'react-icons/md';

import { transactionsAPI, bankAccountsAPI } from 'services/api';
import { useAuth } from 'context/AuthContext';

// --- YARDIMCI FORMATLAR ---
const formatCurrency = (val) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(Number(val) || 0);

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(date);
};

const PAGE_SIZE = 10;

// --- SKELETON LOADER ---
const TransactionSkeleton = () => (
    <div className="flex flex-col gap-6 animate-pulse mt-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[1, 2, 3].map(i => <div key={i} className="h-28 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />)}
        </div>
        <div className="h-14 w-full md:w-1/3 rounded-xl bg-gray-200 dark:bg-navy-700" />
        <div className="flex flex-col gap-4">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-20 w-full rounded-2xl bg-gray-200 dark:bg-navy-700" />)}
        </div>
    </div>
);

const StaffTransactionList = () => {
    // --- AUTH CONTEXT ---
    const { user } = useAuth();

    // --- STATELER ---
    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [bankList, setBankList] = useState([]);
    const [search, setSearch] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [modal, setModal] = useState({ visible: false, data: null });

    // --- API VERİ ÇEKME (HAFTALIK KISITLAMA MANTIĞI EKLENDİ) ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [transRes, bankRes] = await Promise.all([
                transactionsAPI.getAll(),
                bankAccountsAPI.getAll()
            ]);

            if (transRes.success) {
                // Son 7 gün kuralı için tarih sınırı belirleme
                const sevenDaysAgo = new Date();
                sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                sevenDaysAgo.setHours(0, 0, 0, 0);

                // Sadece giriş yapan personele ait VE son 7 güne ait işlemleri al
                const myTransactions = (transRes.transactions || []).filter(t => {
                    const isMyTransaction = String(t.staff_id) === String(user?.id);
                    const tDate = new Date(t.date);
                    const isWithinSevenDays = tDate >= sevenDaysAgo;

                    return isMyTransaction && isWithinSevenDays;
                });

                // Tarihe göre yeniden eskiye sırala
                const sorted = myTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
                setTransactions(sorted);
            }

            if (bankRes.success) {
                setBankList(bankRes.bank_accounts || []);
            }
        } catch (err) {
            console.error("Finans verileri çekilemedi:", err);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user?.id) {
            fetchData();
        }
    }, [fetchData, user?.id]);

    // --- İSTATİSTİKLER VE FİLTRELEME ---
    const stats = useMemo(() => {
        return transactions.reduce((acc, curr) => {
            const amount = parseFloat(curr.amount) || 0;
            if (curr.type === 'income') acc.income += amount;
            else if (curr.type === 'expense') acc.expense += amount;
            return acc;
        }, { income: 0, expense: 0 });
    }, [transactions]);

    const filteredData = useMemo(() => {
        if (!search) return transactions;
        const lowerSearch = search.toLowerCase();
        return transactions.filter(t =>
            (t.title && t.title.toLowerCase().includes(lowerSearch)) ||
            (t.description && t.description.toLowerCase().includes(lowerSearch))
        );
    }, [transactions, search]);

    // Arama yapıldığında sayfayı 1'e al
    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    // --- SAYFALAMA ---
    const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
    const currentItems = useMemo(() => {
        const start = (currentPage - 1) * PAGE_SIZE;
        return filteredData.slice(start, start + PAGE_SIZE);
    }, [filteredData, currentPage]);

    // --- RENDER ---
    if (loading) {
        return <TransactionSkeleton />;
    }

    return (
        <div className="mt-5 flex w-full flex-col gap-6 pb-20 animate-fadeIn">

            {/* 1. ÜST BAŞLIK */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-white p-6 rounded-[20px] shadow-3xl shadow-shadow-500 dark:bg-navy-800 dark:shadow-none border border-gray-100 dark:border-white/10">
                <div className="flex items-center gap-4 w-full">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500 dark:bg-brand-400/10 dark:text-brand-400 flex-none">
                        <MdReceiptLong className="text-3xl" />
                    </div>
                    <div>
                        <h4 className="text-xl font-bold text-navy-700 dark:text-white">Haftalık İşlem Geçmişim</h4>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            Son 7 güne ait gelir (tahsilat) ve gider (avans) hareketleriniz.
                        </p>
                    </div>
                </div>
            </div>

            {/* 2. ÖZET KARTLARI */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Gelir Kartı */}
                <div className="flex items-center p-6 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 border-b-4 border-b-green-500">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50 text-green-500 dark:bg-green-500/10 mr-4">
                        <MdTrendingUp className="text-3xl" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">GELİR / TAHSİLAT</p>
                        <h4 className="text-2xl font-black text-green-500">{formatCurrency(stats.income)}</h4>
                    </div>
                </div>

                {/* Gider Kartı */}
                <div className="flex items-center p-6 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 border-b-4 border-b-red-500">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500 dark:bg-red-500/10 mr-4">
                        <MdTrendingDown className="text-3xl" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">GİDER / AVANS</p>
                        <h4 className="text-2xl font-black text-red-500">{formatCurrency(stats.expense)}</h4>
                    </div>
                </div>

                {/* Net Kartı */}
                <div className="flex items-center p-6 bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/5 border-b-4 border-b-brand-500">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-500 dark:bg-brand-500/10 mr-4">
                        <MdAccountBalanceWallet className="text-3xl" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">NET (7 GÜN)</p>
                        <h4 className={`text-2xl font-black ${(stats.income - stats.expense) >= 0 ? 'text-brand-500' : 'text-red-500'}`}>
                            {formatCurrency(stats.income - stats.expense)}
                        </h4>
                    </div>
                </div>
            </div>

            {/* 3. ARAMA VE LİSTELEME */}
            <div className="bg-white dark:bg-navy-800 rounded-[20px] shadow-sm border border-gray-100 dark:border-white/10 overflow-hidden">

                {/* Arama Çubuğu */}
                <div className="p-5 border-b border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/20">
                    <div className="relative w-full md:w-96">
                        <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Son 7 gündeki işlemlerde ara..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full h-11 pl-11 pr-4 rounded-xl border border-gray-200 bg-white text-sm font-bold text-navy-700 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white transition-all shadow-sm"
                        />
                    </div>
                </div>

                {/* Liste İçeriği */}
                <div className="p-4 md:p-0">
                    {filteredData.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 opacity-60">
                            <MdReceiptLong className="text-6xl text-gray-300 dark:text-gray-600 mb-4" />
                            <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-1">İşlem Bulunamadı</h5>
                            <p className="text-sm font-medium text-gray-500 text-center">Son 7 güne ait herhangi bir işlem kaydı yok.</p>
                        </div>
                    ) : (
                        <>
                            {/* --- MASAÜSTÜ TABLO --- */}
                            <div className="hidden md:block overflow-x-auto p-5">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                    <tr>
                                        <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">İşlem Detayı</th>
                                        <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">Tarih</th>
                                        <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10">Ödeme</th>
                                        <th className="p-4 text-xs font-bold text-gray-400 uppercase tracking-widest border-b border-gray-100 dark:border-white/10 text-right">Tutar</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {currentItems.map((item) => {
                                        const isIncome = item.type === 'income';
                                        const bank = bankList.find(b => b.id === item.bank_account_id);
                                        return (
                                            <tr
                                                key={item.id}
                                                onClick={() => setModal({ visible: true, data: item })}
                                                className="hover:bg-gray-50/50 dark:hover:bg-navy-900/50 transition-colors border-b border-gray-50 dark:border-white/5 last:border-none cursor-pointer group"
                                            >
                                                <td className="p-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className={`h-10 w-10 flex-none rounded-full flex items-center justify-center ${isIncome ? 'bg-green-50 text-green-500 dark:bg-green-500/10' : 'bg-red-50 text-red-500 dark:bg-red-500/10'}`}>
                                                            {isIncome ? <MdTrendingUp className="text-xl" /> : <MdTrendingDown className="text-xl" />}
                                                        </div>
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-bold text-navy-700 dark:text-white group-hover:text-brand-500 transition-colors">{item.title}</span>
                                                            {item.description && <span className="text-[10px] font-medium text-gray-400 line-clamp-1 mt-0.5">{item.description}</span>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-4">
                                                    <div className="flex items-center gap-2 text-sm font-bold text-gray-600 dark:text-gray-300">
                                                        <MdCalendarToday className="text-gray-400" />
                                                        {formatDate(item.date)}
                                                    </div>
                                                </td>
                                                <td className="p-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${item.payment_method === 'card' ? 'bg-brand-50 text-brand-500 border-brand-100 dark:bg-brand-400/10 dark:text-brand-400 dark:border-brand-800' : 'bg-green-50 text-green-600 border-green-100 dark:bg-green-500/10 dark:text-green-400 dark:border-green-800'}`}>
                              {item.payment_method === 'card' ? <MdCreditCard /> : <MdMoney />}
                                {item.payment_method === 'card' ? (bank?.name || 'Kredi Kartı') : 'Nakit'}
                            </span>
                                                </td>
                                                <td className="p-4 text-right">
                            <span className={`text-base font-black ${isIncome ? 'text-green-500' : 'text-red-500'}`}>
                              {isIncome ? '+' : '-'} {formatCurrency(item.amount)}
                            </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            {/* --- MOBİL GÖRÜNÜM (KARTLAR) --- */}
                            <div className="flex flex-col gap-4 md:hidden">
                                {currentItems.map((item) => {
                                    const isIncome = item.type === 'income';
                                    const bank = bankList.find(b => b.id === item.bank_account_id);
                                    return (
                                        <div
                                            key={item.id}
                                            onClick={() => setModal({ visible: true, data: item })}
                                            className="flex flex-col p-5 rounded-2xl bg-white dark:bg-navy-900 border border-gray-100 dark:border-white/5 shadow-sm active:scale-[0.98] transition-all"
                                        >
                                            <div className="flex justify-between items-start mb-3">
                                                <div className="flex items-center gap-3">
                                                    <div className={`h-10 w-10 flex-none rounded-full flex items-center justify-center ${isIncome ? 'bg-green-50 text-green-500 dark:bg-green-500/10' : 'bg-red-50 text-red-500 dark:bg-red-500/10'}`}>
                                                        {isIncome ? <MdTrendingUp className="text-xl" /> : <MdTrendingDown className="text-xl" />}
                                                    </div>
                                                    <span className="text-sm font-bold text-navy-700 dark:text-white leading-tight">
                            {item.title}
                          </span>
                                                </div>
                                                <span className={`text-sm font-black whitespace-nowrap ml-2 ${isIncome ? 'text-green-500' : 'text-red-500'}`}>
                          {isIncome ? '+' : '-'} {formatCurrency(item.amount)}
                        </span>
                                            </div>

                                            <div className="flex items-center justify-between mt-2 pt-3 border-t border-gray-100 dark:border-white/10">
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                          <MdCalendarToday /> {formatDate(item.date).split(' ')[0]}
                        </span>
                                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${item.payment_method === 'card' ? 'bg-brand-50 text-brand-500 border-brand-100 dark:bg-brand-400/10 dark:text-brand-400 dark:border-brand-800' : 'bg-green-50 text-green-600 border-green-100 dark:bg-green-500/10 dark:text-green-400 dark:border-green-800'}`}>
                          {item.payment_method === 'card' ? <MdCreditCard /> : <MdMoney />}
                                                    {item.payment_method === 'card' ? (bank?.name || 'Kredi Kartı') : 'Nakit'}
                        </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* 4. SAYFALAMA (PAGINATION) */}
                {!loading && totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between p-5 border-t border-gray-100 dark:border-white/10 bg-gray-50/50 dark:bg-navy-900/20 gap-4">
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
              <span className="font-bold text-navy-700 dark:text-white">{filteredData.length}</span> işlemden <span className="font-bold text-navy-700 dark:text-white">{(currentPage-1)*PAGE_SIZE + 1}-{Math.min(currentPage*PAGE_SIZE, filteredData.length)}</span> gösteriliyor.
            </span>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                            >
                                <MdKeyboardArrowLeft className="text-2xl" />
                            </button>

                            <div className="flex h-10 min-w-[40px] items-center justify-center rounded-xl bg-brand-500 text-white text-sm font-bold shadow-md px-3">
                                {currentPage}
                            </div>

                            <button
                                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 disabled:opacity-40 hover:bg-gray-50 dark:border-white/10 dark:bg-navy-800 dark:hover:bg-navy-700 transition-all active:scale-95"
                            >
                                <MdKeyboardArrowRight className="text-2xl" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* --- DETAY MODALI (GLASSMORPHISM) --- */}
            {modal.visible && modal.data && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-navy-900/60 backdrop-blur-sm p-4 animate-fadeIn" onClick={() => setModal({ visible: false, data: null })}>
                    <div className="w-full max-w-md bg-white dark:bg-navy-800 rounded-[24px] shadow-2xl overflow-hidden transform transition-all" onClick={e => e.stopPropagation()}>

                        <div className={`p-6 text-white relative ${modal.data.type === 'income' ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-red-500 to-red-600'}`}>
                            <button onClick={() => setModal({ visible: false, data: null })} className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors">
                                <MdClose className="text-xl" />
                            </button>
                            <div className="flex items-center gap-3 mb-2">
                                <div className="h-10 w-10 flex-none rounded-full bg-white/20 flex items-center justify-center">
                                    {modal.data.type === 'income' ? <MdTrendingUp className="text-2xl" /> : <MdTrendingDown className="text-2xl" />}
                                </div>
                                <h3 className="text-lg font-bold leading-tight pr-8">{modal.data.title}</h3>
                            </div>
                            <p className="text-3xl font-black mt-4">
                                {modal.data.type === 'income' ? '+' : '-'} {formatCurrency(modal.data.amount)}
                            </p>
                        </div>

                        <div className="p-6 flex flex-col gap-4">
                            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-navy-900/50 border border-gray-100 dark:border-white/5">
                                <div className="h-10 w-10 flex-none rounded-full bg-brand-50 dark:bg-brand-500/10 text-brand-500 flex items-center justify-center text-xl"><MdCalendarToday /></div>
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase">Tarih</p>
                                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                                        {formatDate(modal.data.date)}
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 dark:bg-navy-900/50 border border-gray-100 dark:border-white/5">
                                <div className="h-10 w-10 flex-none rounded-full bg-blue-50 dark:bg-blue-500/10 text-blue-500 flex items-center justify-center text-xl">
                                    {modal.data.payment_method === 'card' ? <MdCreditCard /> : <MdMoney />}
                                </div>
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase">Ödeme Yöntemi</p>
                                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                                        {modal.data.payment_method === 'card' ? (bankList.find(b => b.id === modal.data.bank_account_id)?.name || 'Kredi Kartı / Havale') : 'Nakit Ödeme'}
                                    </p>
                                </div>
                            </div>

                            {modal.data.description && (
                                <div className="mt-2">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase mb-1 ml-1">Açıklama</p>
                                    <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-400/10 border border-brand-100 dark:border-brand-500/20 relative">
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-400 rounded-l-xl" />
                                        <p className="text-sm font-medium text-navy-700 dark:text-gray-200 italic leading-relaxed pl-2">
                                            "{modal.data.description}"
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="p-5 pt-0 flex justify-end">
                            <button
                                onClick={() => setModal({ visible: false, data: null })}
                                className="px-6 py-2.5 rounded-xl bg-gray-100 dark:bg-navy-700 text-gray-600 dark:text-white text-sm font-bold hover:bg-gray-200 dark:hover:bg-navy-600 transition-colors w-full"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
            `}</style>

        </div>
    );
};

export default StaffTransactionList;